import React, { useState, useEffect } from 'react';
import { Select, Checkbox } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
export interface VariableDefinition {
  variable: string;       // {{1}}, {{2}}, etc.
  name: string;          // Nome descritivo (ex: "Nome do Cliente")
  type: 'text' | 'number' | 'date' | 'email' | 'phone' | 'url';
  description?: string;  // Descrição opcional
  required?: boolean;    // Se é obrigatório
  example?: string;      // Exemplo de valor
}

interface VariableDefinitionEditorProps {
  value: VariableDefinition[];
  onChange: (variables: VariableDefinition[]) => void;
  detectedVariables?: string[];  // Variáveis detectadas no conteúdo do template
  maxVariables?: number;
}

const VARIABLE_TYPES = [
  { value: 'text', label: 'Texto', description: 'Qualquer texto' },
  { value: 'number', label: 'Número', description: 'Valores numéricos' },
  { value: 'date', label: 'Data', description: 'Datas (DD/MM/YYYY)' },
  { value: 'email', label: 'Email', description: 'Endereços de e-mail' },
  { value: 'phone', label: 'Telefone', description: 'Números de telefone' },
  { value: 'url', label: 'URL', description: 'Links e URLs' },
];

export const VariableDefinitionEditor: React.FC<VariableDefinitionEditorProps> = ({
  value,
  onChange,
  detectedVariables = [],
  maxVariables = 10,
}) => {
  const [variables, setVariables] = useState<VariableDefinition[]>(value);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // ✅ Sincronizar com prop value quando mudar (apenas se for diferente)
  useEffect(() => {
    // Comparar usando JSON para evitar re-renders desnecessários
    if (JSON.stringify(value) !== JSON.stringify(variables)) {
      setVariables(value);
    }
  }, [JSON.stringify(value)]);

  // Adicionar nova variável
  const addVariable = () => {
    if (variables.length >= maxVariables) return;

    // Encontrar próximo número disponível
    const usedNumbers = variables.map((v) => parseInt(v.variable.replace(/[{}]/g, '')));
    let nextNumber = 1;
    while (usedNumbers.includes(nextNumber)) {
      nextNumber++;
    }

    const newVariable: VariableDefinition = {
      variable: `{{${nextNumber}}}`,
      name: '',
      type: 'text',
      required: true,
    };

    const newVariables = [...variables, newVariable];
    setVariables(newVariables);
    onChange(newVariables); // ✅ Chamar onChange explicitamente
    setExpandedIndex(variables.length);
  };

  // Remover variável
  const removeVariable = (index: number) => {
    const newVariables = variables.filter((_, i) => i !== index);
    setVariables(newVariables);
    onChange(newVariables); // ✅ Chamar onChange explicitamente
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  // Atualizar campo de variável
  const updateVariable = (index: number, field: keyof VariableDefinition, value: any) => {
    const newVariables = [...variables];
    newVariables[index] = { ...newVariables[index], [field]: value };
    setVariables(newVariables);
    onChange(newVariables); // ✅ Chamar onChange explicitamente
  };

  // Checar se variável está sendo usada
  const isVariableUsed = (variable: string) => {
    return detectedVariables.includes(variable);
  };

  // Variáveis detectadas mas não definidas
  const undefinedVariables = detectedVariables.filter(
    (detected) => !variables.some((v) => v.variable === detected)
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
            Variáveis do Template
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
            Defina as variáveis que serão substituídas no envio
          </p>
        </div>
        <button
          type="button"
          onClick={addVariable}
          disabled={variables.length >= maxVariables}
          className="px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-2 disabled:opacity-50"
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: 'var(--theme-input-border)',
            color: 'var(--theme-card-text)',
          }}
        >
          <Icon name="plus" className="w-4 h-4" />
          Adicionar Variável
        </button>
      </div>

      {/* Warning sobre variáveis não definidas */}
      {undefinedVariables.length > 0 && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg border"
          style={{
            backgroundColor: 'var(--theme-warning-background)',
            borderColor: 'var(--theme-warning-color)',
          }}
        >
          <Icon name="alert-circle" className="w-4 h-4 mt-0.5" style={{ color: 'var(--theme-warning-color)' }} />
          <div className="flex-1">
            <p className="text-sm font-medium" style={{ color: 'var(--theme-warning-color)' }}>
              Variáveis detectadas mas não definidas
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
              {undefinedVariables.join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Lista de variáveis */}
      {variables.length === 0 ? (
        <div
          className="text-center py-8 rounded-lg border-2 border-dashed"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <Icon name="code" className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--theme-muted-text)' }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhuma variável definida
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
            Clique em "Adicionar Variável" para começar
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {variables.map((variable, index) => (
            <div
              key={index}
              className="rounded-lg border"
              style={{
                borderColor: isVariableUsed(variable.variable)
                  ? 'var(--theme-success-color)'
                  : 'var(--theme-card-border)',
                backgroundColor: 'var(--theme-input-background)',
              }}
            >
              {/* Header Compacto */}
              <button
                type="button"
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="w-full px-4 py-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="px-2 py-1 rounded text-xs font-mono font-medium"
                    style={{
                      backgroundColor: isVariableUsed(variable.variable)
                        ? 'var(--theme-success-background)'
                        : 'var(--theme-card-background)',
                      color: isVariableUsed(variable.variable)
                        ? 'var(--theme-success-color)'
                        : 'var(--theme-muted-text)',
                    }}
                  >
                    {variable.variable}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {variable.name || 'Sem nome'}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                      {VARIABLE_TYPES.find((t) => t.value === variable.type)?.label}
                      {variable.required && ' • Obrigatório'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isVariableUsed(variable.variable) && (
                    <span
                      className="px-2 py-0.5 text-xs rounded"
                      style={{
                        backgroundColor: 'var(--theme-success-background)',
                        color: 'var(--theme-success-color)',
                      }}
                    >
                      Em uso
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeVariable(index);
                    }}
                    className="p-1 rounded transition-colors"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                </div>
              </button>

              {/* Detalhes Expandidos */}
              {expandedIndex === index && (
                <div
                  className="px-4 pb-4 space-y-3 border-t"
                  style={{ borderColor: 'var(--theme-card-border)' }}
                >
                  {/* Nome */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
                      Nome da Variável <span style={{ color: 'var(--theme-error-color)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={variable.name}
                      onChange={(e) => updateVariable(index, 'name', e.target.value)}
                      placeholder="Ex: Nome do Cliente"
                      className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                    />
                  </div>

                  {/* Tipo */}
                  <Select
                    label="Tipo de Dado"
                    options={VARIABLE_TYPES.map((type) => ({
                      value: type.value,
                      label: `${type.label} - ${type.description}`,
                    }))}
                    value={variable.type}
                    onChange={(val) => updateVariable(index, 'type', val)}
                  />

                  {/* Descrição */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
                      Descrição (opcional)
                    </label>
                    <input
                      type="text"
                      value={variable.description || ''}
                      onChange={(e) => updateVariable(index, 'description', e.target.value)}
                      placeholder="Informações adicionais sobre esta variável"
                      className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                    />
                  </div>

                  {/* Exemplo */}
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
                      Valor de Exemplo
                    </label>
                    <input
                      type="text"
                      value={variable.example || ''}
                      onChange={(e) => updateVariable(index, 'example', e.target.value)}
                      placeholder="Ex: João Silva"
                      className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                    />
                  </div>

                  {/* Obrigatório */}
                  <Checkbox
                    label="Esta variável é obrigatória"
                    checked={variable.required ?? true}
                    onChange={(checked) => updateVariable(index, 'required', checked)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info Footer */}
      <div
        className="rounded-lg border p-3"
        style={{
          borderColor: 'var(--theme-card-border)',
          backgroundColor: 'var(--theme-card-background)',
        }}
      >
        <div className="flex items-start gap-2">
          <Icon name="alert-circle" className="w-4 h-4 mt-0.5" style={{ color: 'var(--theme-info-color)' }} />
          <div className="flex-1">
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              <strong style={{ color: 'var(--theme-card-text)' }}>Como usar:</strong> Use a sintaxe{' '}
              <code
                className="px-1 py-0.5 rounded font-mono text-xs"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  color: 'var(--theme-primary)',
                }}
              >
                {'{{1}}'}
              </code>
              ,{' '}
              <code
                className="px-1 py-0.5 rounded font-mono text-xs"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  color: 'var(--theme-primary)',
                }}
              >
                {'{{2}}'}
              </code>
              , etc. no conteúdo do seu template. No envio, esses valores serão substituídos pelos dados reais.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
